import { bootstrapAccount } from '@/lib/api';
import { auth } from '@/lib/firebase';
import { clearAll as clearServiceQueue } from '@/lib/serviceQueue';
import { clearQueue as clearTelemetryQueue, flushQueue, track } from '@/lib/telemetry';
import { useQueryClient } from '@tanstack/react-query';
import { signOut as fbSignOut, onAuthStateChanged, type User } from 'firebase/auth';
import { createContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { signInWithApple } from './signInWithApple';
import { signInWithGoogle } from './signInWithGoogle';

export type AuthProvider = 'google' | 'apple';

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signIn: (provider: AuthProvider) => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const queryClient = useQueryClient();
  const bootstrappedRef = useRef<boolean>(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (next) => {
      setUser(next);
      setLoading(false);
      if (next && !bootstrappedRef.current) {
        bootstrappedRef.current = true;
        bootstrapAccount().catch((err: unknown) => {
          console.error('[AuthProvider] bootstrap failed', err);
        });
        // Flush pre-auth queued telemetry (app_opened, signed_in, etc.) now
        // that we have an authenticated connection. Fire-and-forget.
        flushQueue().catch(() => {
          /* swallow */
        });
      }
      if (!next) {
        bootstrappedRef.current = false;
      }
    });
    return unsub;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      signIn: async (provider) => {
        // Capture 'returning' before the auth call — if a user is already
        // cached in Firebase Auth they're returning (e.g. token refresh).
        // After the call completes, queue the signed_in event pre-flush.
        const wasSignedIn = !!auth.currentUser;
        if (provider === 'google') {
          await signInWithGoogle();
        } else {
          await signInWithApple();
        }
        track('signed_in', {
          method: provider === 'google' ? 'google' : 'passkey',
          returning: wasSignedIn,
        });
      },
      signOut: async () => {
        await fbSignOut(auth);
        // Avoid leaking previous user's cached gg-api responses.
        queryClient.clear();
        // §00 SOC2 BLOCKER: clear offline queue to prevent User A's unsynced
        // entries from replaying under User B on a shared device.
        // Best-effort: don't block sign-out if IndexedDB fails.
        clearServiceQueue().catch((err: unknown) => {
          console.warn('[AuthProvider] clearServiceQueue on signOut failed', {
            // PII-safe: only log that it failed, not the queue contents.
            error: err instanceof Error ? err.message : 'unknown',
          });
        });
        // Clear telemetry queue on sign-out — prevent cross-user event leaks.
        clearTelemetryQueue().catch(() => {
          /* swallow */
        });
      },
    }),
    [user, loading, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
